import React, { useEffect, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import { TopPick, tipsterApi } from '../services/tipsterService';
import { translateEvent } from '../utils/translateEvent';
import '../styles/sections.scss';

interface TopPicksSectionProps {
  limit?: number;
  days?: number;
  showRangeSwitch?: boolean;
  showSubtitle?: boolean;
}

const TopPicksSection: React.FC<TopPicksSectionProps> = ({ limit = 10, days: initialDays = 3, showRangeSwitch = false, showSubtitle = true }) => {
  const { t } = useTranslation();
  const [picks, setPicks] = useState<TopPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(initialDays);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    tipsterApi.getTopPicks(limit, days)
      .then(setPicks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit, days]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="top-section">
      <div className="top-section__header">
        <h2 className="top-section__title">{t.hub.lastTopPicks}</h2>
        {showSubtitle && (
          <span className="top-section__subtitle">
            {t.hub.biggestOddsWon.replace('{days}', String(days))}
          </span>
        )}
        {showRangeSwitch && (
          <div className="top-section__range-switch">
            <button
              type="button"
              className={`top-section__range-btn ${days === 3 ? 'top-section__range-btn--active' : ''}`}
              onClick={() => setDays(3)}
            >
              {t.hub.last3Days}
            </button>
            <button
              type="button"
              className={`top-section__range-btn ${days === 7 ? 'top-section__range-btn--active' : ''}`}
              onClick={() => setDays(7)}
            >
              {t.hub.last7Days}
            </button>
            <button
              type="button"
              className={`top-section__range-btn ${days === 30 ? 'top-section__range-btn--active' : ''}`}
              onClick={() => setDays(30)}
            >
              {t.hub.last30Days}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="top-section__loading">{t.common.loading}</div>
      ) : picks.length === 0 ? (
        <div className="top-section__empty">{t.hub.noWinningPicks}</div>
      ) : (
        <div className="top-picks__list">
          {picks.map((pick, index) => (
            <div key={index} className="top-picks__card">
              <div className="top-picks__odds-badge">{pick.odds.toFixed(2)}</div>

              <div className="top-picks__match">
                <span className="top-picks__teams">
                  {translateEvent(pick.home_team, t.eventsDictionary)} <span className="top-picks__vs">{t.common.vs}</span> {translateEvent(pick.away_team, t.eventsDictionary)}
                </span>
                <span className="top-picks__event">{pick.event}</span>
              </div>

              <div className="top-picks__meta">
                <span
                  className="top-picks__tipster"
                  onClick={() => navigate(`/experts/${pick.tipster_id}`)}
                >
                  {pick.tipster_name}
                  {pick.tipster_verified && <FaCheckCircle className="top-picks__verified" />}
                </span>
                <span className="top-picks__date">{formatDate(pick.game_datetime)}</span>
              </div>

              <span className="top-picks__win-badge">{t.pickResults.win}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopPicksSection;
